import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  ShieldCheck,
  Flame,
  ChevronRight,
  Trophy,
  Dumbbell,
} from 'lucide-react';

/**
 * 마케팅 유입용 랜딩 페이지 (비로그인 첫 화면)
 * - 헬스 초보자 타겟 원페이지 랜딩
 * - 다크 테마 + CSS 변수 토큰(var(--accent) 등) 사용, 모바일 우선(max-w-2xl)
 * - MainLayout(헤더/하단 네비) 바깥에서 단독 전체화면으로 렌더링됨
 */
export default function Landing() {
  const navigate = useNavigate();

  const goRegister = () => navigate('/register');
  const goLogin = () => navigate('/login');

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] relative overflow-x-clip">
      {/* 배경 글로우 (앱 메인 레이아웃과 동일한 앰비언트 분위기) */}
      <div
        className="absolute top-[-15%] left-[-10%] w-[70vw] h-[70vw] rounded-full opacity-[0.08] pointer-events-none z-0"
        style={{
          background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      {/* 상단 바 */}
      <header className="relative z-10 max-w-2xl mx-auto px-5 pt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[var(--accent)]/15 flex items-center justify-center">
            <Flame size={18} className="text-[var(--accent)]" />
          </div>
          <span className="text-base font-extrabold tracking-tight">헬게이터</span>
        </div>
        <button
          onClick={goLogin}
          className="text-sm font-medium text-[var(--text-secondary)] px-3 py-1.5 rounded-full border border-[var(--border)] hover:border-[var(--accent)] transition-colors"
        >
          로그인
        </button>
      </header>

      {/* 히어로 */}
      <section className="relative z-10 max-w-2xl mx-auto px-5 pt-8 pb-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* 마스코트 */}
          <motion.img
            src="/images/clay/baalzebul.png"
            alt="헬게이터 마스코트 바알시불"
            className="w-40 h-40 mx-auto object-contain select-none"
            style={{ filter: 'drop-shadow(0 12px 18px rgba(0,0,0,0.5))' }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            draggable={false}
          />

          <span className="inline-flex items-center gap-1.5 mt-4 px-3 py-1 rounded-full text-xs font-semibold bg-[var(--accent)]/12 text-[var(--accent)]">
            <Sparkles size={13} />
            헬스 초보자를 위한 운동 게임
          </span>

          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold leading-tight">
            운동이 게임이 되는 순간
          </h1>

          <p className="mt-4 text-[15px] leading-relaxed text-[var(--text-secondary)] max-w-md mx-auto">
            헬스가 처음이어도 괜찮아요. 오늘 딱 한 세트만 기록하면
            내 캐릭터가 성장하고 레벨이 오릅니다.
          </p>

          {/* 주 CTA */}
          <div className="mt-7 flex flex-col items-center gap-3">
            <button
              onClick={goRegister}
              className="w-full max-w-xs py-3.5 rounded-2xl text-base font-bold bg-[var(--accent)] text-[var(--bg)] shadow-lg active:scale-[0.98] transition-transform"
              style={{ boxShadow: '0 8px 20px rgba(255, 91, 91, 0.25)' }}
            >
              무료로 시작하기
            </button>
            <button
              onClick={goLogin}
              className="text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
            >
              이미 계정이 있어요
            </button>
          </div>

          <p className="mt-4 text-xs text-[var(--text-muted)]">
            가입 30초 · 신용카드 필요 없음
          </p>
        </motion.div>
      </section>

      {/* 기능 섹션 */}
      <section className="relative z-10 max-w-2xl mx-auto px-5 pt-8 pb-4">
        <h2 className="text-center text-lg font-bold mb-5 text-[var(--text)]">
          왜 헬게이터인가요?
        </h2>

        <div className="space-y-3">
          <FeatureCard
            icon={<Trophy size={22} className="text-[var(--accent)]" />}
            title="기록할수록, 캐릭터가 큰다"
            body="무게와 횟수를 남기면 경험치가 쌓여요. 부위별 레벨을 올리고 스킬을 해금하며 맵을 탐험합니다. 운동이 지루하지 않은 이유예요."
            tags={['부위별 레벨', '스킬 해금', '맵 탐험']}
          />
          <FeatureCard
            icon={<ShieldCheck size={22} className="text-[var(--accent)]" />}
            title="얼마나 들어야 할지, 대신 알려줘요"
            body="성별과 체중에 맞춘 추천 무게, 동작별 폼 가이드, 운동 전 워밍업 안내까지. 초보자가 다치지 않고 시작할 수 있게 설계했어요."
            tags={['추천 무게', '폼 가이드', '워밍업 안내']}
          />
          <FeatureCard
            icon={<Flame size={22} className="text-[var(--accent)]" />}
            title="2주 전신 챌린지로 습관 만들기"
            body="하루 짧게, 초보자 맞춤 전신 프로그램으로 운동 루틴을 시작하세요. 작은 성공이 쌓이면 어느새 헬스가 익숙해집니다."
            tags={['초보자 맞춤', '전신 운동', '2주 완성']}
          />
        </div>
      </section>

      {/* 마지막 CTA */}
      <section className="relative z-10 max-w-2xl mx-auto px-5 pt-6 pb-14">
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 text-center"
          style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}
        >
          <div className="w-11 h-11 mx-auto rounded-2xl bg-[var(--accent)]/12 flex items-center justify-center mb-3">
            <Dumbbell size={22} className="text-[var(--accent)]" />
          </div>
          <p className="text-lg font-bold text-[var(--text)]">
            오늘부터, 운동이 게임이 됩니다
          </p>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            첫 기록 한 번이면 시작이에요.
          </p>
          <button
            onClick={goRegister}
            className="mt-5 w-full max-w-xs mx-auto flex items-center justify-center gap-1 py-3.5 rounded-2xl text-base font-bold bg-[var(--accent)] text-[var(--bg)] active:scale-[0.98] transition-transform"
            style={{ boxShadow: '0 8px 20px rgba(255, 91, 91, 0.25)' }}
          >
            무료로 시작하기
            <ChevronRight size={18} />
          </button>
        </div>

        {/* 푸터 */}
        <p className="mt-8 text-center text-xs text-[var(--text-muted)]">
          헬게이터 · 헬스의 게임화
        </p>
      </section>
    </div>
  );
}

/** 기능 소개 카드 (리스트형 가로 카드 — 아이콘 좌측 + 정보 우측) */
function FeatureCard({
  icon,
  title,
  body,
  tags,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  tags: string[];
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4 }}
      className="flex gap-4 p-4 rounded-2xl bg-[var(--card)] border border-[var(--border)] mx-1"
      style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}
    >
      <div className="w-11 h-11 shrink-0 rounded-xl bg-[var(--accent)]/10 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[15px] font-bold text-[var(--text)]">{title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-secondary)]">
          {body}
        </p>
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--surface)] text-[var(--text-muted)] font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
